// @ts-check

/**
 * @typedef {Object} FfNavigationTabCustomProperties
 * @property {(scopeElmt:HTMLElement) => void} ffInit
 * @property {(contents:{forEach: Function, length: number}) => void} ffInitWithContents
 * @property {Boolean} ffIsActive
 * @property {Function} ffToggleContentsVisibility
 * @property {Function} ffHideContents
 * @property {Function} ffShowContents
 * @typedef {FfNavigationTabCustomProperties & HTMLElement} FfNavigationTab
 */
if (!customElements.get("ff-navigation-tab")) {
  class FfNavigationTab extends HTMLElement {
    /**
     * @type {Boolean}
     */
    ffIsActive;
    /**
     * @type {ResizeObserver | null}
     */
    resizeObserver = null;
    constructor() {
      super();

      const { ffContentSelector } = this.dataset;

      if (ffContentSelector) {
        this.ffContentSelector = ffContentSelector;
      } else {
        console.log("Missing content selector");
      }
    }

    /**
     *
     * @param {{forEach: Function, length: number}} contents
     */
    ffInitWithContents(contents) {
      this.ffContents = contents;

      this.ffContents?.forEach((content) => {
        const ffFAQSection = content.querySelector('[class^="ff-faqs-section"]');
        ffFAQSection.style.display = "none"

        content.addEventListener('transitionend', () => {
          
          if (this.ffIsActive) {
            content.style.height = 'unset';
          }

          if (content.classList.contains("ff-tabs-builder_hide-tab-content")) {
            ffFAQSection.style.display = "none"
          }
        })
      });

      if (this.ffContents.length == 0)
        throw new Error(
          `No contents for ${this.ffContentSelector} were found.`
        );

      if (this.dataset.ffInitialActive == "true") {
        this.ffShowContents();
      } else {
        this.ffHideContents();
      }
      this.ffContents?.forEach((content) => {
        content.style.visibility = "visible";
      })
    }

    /**
     * The scope to hide or show contents (scope for the query selector)
     * @param { HTMLElement } scopeElmt
     */
    ffInit(scopeElmt) {
      /**
       * @type {{forEach: Function, length: number}}
       */
      this.ffContents = scopeElmt.querySelectorAll(this.ffContentSelector);

      if (this.ffContents.length == 0)
        throw new Error(
          `No contents for ${this.ffContentSelector} were found.`
        );

      if (this.dataset.ffInitialActive == "true") {
        this.ffShowContents();
      } else {
        this.ffHideContents();
      }
    }

    ffToggleContentsVisibility = () => {
      if (this.ffIsActive) {
        this.ffHideContents();
      } else {
        this.ffShowContents();
      }
    };

    ffHideContents = () => {
      this.ffIsActive = false;

      const { ffActiveClass, ffShowClass, ffHideClass } = this.dataset;

      if (ffActiveClass) {
        this.classList.remove(ffActiveClass);
      }

      this.ffContents?.forEach((content) => {
        const ffFAQSection = content.querySelector('[class^="ff-faqs-section"]');
        const ffTabHeight = content.scrollHeight - ffFAQSection.scrollHeight + 1;

        content.style.height = content.scrollHeight + 'px';
        content.offsetHeight;

        content.style.height = ffTabHeight + 'px';

        if (ffShowClass) {
          content.classList.remove(ffShowClass);
        }

        if (ffHideClass) {
          content.classList.add(ffHideClass);
        }

        if (!ffHideClass && !ffShowClass) {
           content.style.display = "none";
        }
        
        const ctTabs = content.querySelectorAll("ct-tab.active_parent");
        ctTabs.forEach((tab) => {
          const button = tab.querySelector("button");
          if (button) button.click();
        });
      });
    };

    ffShowContents = () => {
      this.ffIsActive = true;
      const { ffActiveClass, ffShowClass, ffHideClass } = this.dataset;

      if (ffActiveClass) {
        this.classList.add(ffActiveClass);
      }

      this.ffContents?.forEach((content) => {
        if (ffHideClass) {
          content.classList.remove(ffHideClass);
        }

        if (ffShowClass) {
          content.classList.add(ffShowClass);
        }

        if (!ffShowClass && !ffHideClass) {
           content.style.display = "";
        }

        const ffFAQSection = content.querySelector('[class^="ff-faqs-section"]');
        const ffTabHeight = content.scrollHeight - ffFAQSection.scrollHeight + 1;
        ffFAQSection.style.display = "block"

        content.style.height = ffTabHeight + 'px';
        content.style.overflow = 'hidden';
        content.style.transition = 'height 0.2s ease';
        content.style.height = content.scrollHeight + 'px';
      });
    };
  }

  customElements.define("ff-navigation-tab", FfNavigationTab);

  class FfClassesToggler extends FfNavigationTab {
    constructor() {
      super();

      this.ffInit();
    }

    ffInit() {
      /**
       * @type {NodeListOf<HTMLElement>}
       */
      this.ffContents = document.querySelectorAll(this.ffContentSelector);
      this.addEventListener("click", this.ffToggleContentsVisibility);

      if (this.dataset.ffInitialActive == "true") {
        this.ffShowContents();
      } else {
        this.ffHideContents();
      }
    }
  }

  customElements.define("ff-classes-toggler", FfClassesToggler);
}

/**
 * @typedef {Object} FfNavigationTabsCustomProperties
 * @property {() => Promise<void>} ffInit
 * @property {Function} ffHideDefaultContents
 * @typedef {FfNavigationTabsCustomProperties & HTMLElement} FfNavigationTabs
 */
if (!customElements.get("ff-navigation-tabs")) {
  class FfNavigationTabs extends HTMLElement {
    constructor() {
      super();

      this.ffInit();
    }

    ffInit() {
      const { ffDisabled, ffDefaultContentSelector } = this.dataset;

      if (ffDisabled != "true") {
        if (ffDefaultContentSelector) {
          /**
           * @type {NodeListOf<HTMLElement>}
           */
          this.ffDefaultContents = this.querySelectorAll(
            ffDefaultContentSelector
          );
        }

        /**
         * @type {NodeListOf<FfNavigationTab>}
         */
        const tabsEl = this.querySelectorAll(`[data-ff-tab='${this.id}']`);

        if (tabsEl.length == 0)
          throw new Error("No tabs for this navigation tabs were found.");

        //Limits only one tab to be active on initialization removing the data-ff-initial-active
        let foundActiveTab = false;

        tabsEl.forEach((tabEl) => {
          if (tabEl.dataset.ffInitialActive == "true") {
            if (foundActiveTab == true) {
              delete tabEl.dataset.ffInitialActive;
            }
            foundActiveTab = true;
          }

          tabEl.ffInit(this);
          const { ffCanToggle } = tabEl.dataset;

          if (ffCanToggle == "true") {
            tabEl.addEventListener("click", () => {
              const { ffIsActive } = tabEl; //We keep the record before being hidden for the toggle
              tabEl.ffIsActive = ffIsActive; //Record value restored
              this.ffHideDefaultContents();
              tabEl.ffToggleContentsVisibility();
            });
          } else {
            tabEl.addEventListener("click", () => {
              this.ffHideDefaultContents();
              tabEl.ffShowContents();
            });
          }
        });

        if (foundActiveTab) {
          this.ffHideDefaultContents();
        }
      }
    }

    ffHideDefaultContents() {
      this.ffDefaultContents?.forEach((content) => {
        content.remove();
      });
    }
  }

  customElements.define("ff-navigation-tabs", FfNavigationTabs);

  class FfNavigationTabsBuilder extends HTMLElement {
    constructor() {
      super();

      this.ffInit();
    }

    ffInit() {
      this.ffScopeElmt = this.closest("main");

      this.ffScopeElmt?.classList.add("ff-tabs-builder-initialized");

      /**
       * @type {NodeListOf<FfNavigationTab>}
       */
      const tabsEl = this.querySelectorAll(`[data-ff-tab='${this.id}']`);

      //Limits only one tab to be active on initialization removing the data-ff-initial-active
      let foundActiveTab = false;
      //lastSectionIterated serves to select the next siblings to the tabs-builder section(parent)
      let lastSectionIterated =
        this.closest(".shopify-section")?.nextElementSibling;

      tabsEl.forEach((tabEl) => {
        if (tabEl.dataset.ffInitialActive == "true") {
          if (foundActiveTab == true) {
            tabEl.dataset.ffInitialActive = "false";
          }
          foundActiveTab = true;
        }

        tabEl.ffInitWithContents([lastSectionIterated]);

        lastSectionIterated?.addEventListener("click", (e) => {
          if (e.target == e.currentTarget) {
            const { ffIsActive } = tabEl; //We keep the record before being hidden for the toggle
            tabEl.ffIsActive = ffIsActive; //Record value restored
            this.ffHideDefaultContents();
            tabEl.ffToggleContentsVisibility();
          }
        });

        lastSectionIterated = lastSectionIterated.nextElementSibling;

        const { ffCanToggle } = tabEl.dataset;

        if (ffCanToggle == "true") {
          tabEl.addEventListener("click", () => {
            const { ffIsActive } = tabEl; //We keep the record before being hidden for the toggle
            tabEl.ffIsActive = ffIsActive; //Record value restored
            this.ffHideDefaultContents();
            tabEl.ffToggleContentsVisibility();
          });
        } else {
          tabEl.addEventListener("click", () => {
            this.ffHideDefaultContents();
            tabEl.ffShowContents();
          });
        }
      }, this);

      if (foundActiveTab) {
        this.ffHideDefaultContents();
      }
    }

    ffHideDefaultContents() {
      this.ffDefaultContents?.forEach((content) => {
        content.remove();
      });
    }
  }

  customElements.define("ff-navigation-tabs-builder", FfNavigationTabsBuilder);
}

if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", () => {
    requestAnimationFrame(() => {
      document.querySelectorAll("ff-navigation-tabs").forEach((tabs) => {
        tabs.ffInit();
      });
    });
  });
}